import { defineStore } from 'pinia';
import { computed, ref, shallowRef } from 'vue';
import type {
  CommunicationKitUiModel,
  CommunicationKitLocale,
} from '@client/feature/communication-kit/domain/ui-model/communication-kit.ui-model';
import type { AtLeastOne } from '@kiplin/web-common/utils';
import { MissingCommunicationKitTemplateError } from '@client/feature/communication-kit/domain/error/missing-communication-kit-template.error';
import { CommunicationKitFormat } from '@client/feature/communication-kit/domain/constant/communication-kit-format.constant';
import { MissingCommunicationKitConfigurationError } from '@client/feature/communication-kit/domain/error/missing-communication-kit-configuration.error';
import type { CommunicationKitTemplateConfiguration } from '@client/feature/communication-kit/domain/model/communication-kit-template-configuration.model';
import type { CommunicationKitCustomisationPreviewElements } from '@client/feature/communication-kit/domain/model/communication-kit-customisation-preview-elements.model';
import { MissingCommunicationKitFormatError } from '@client/feature/communication-kit/domain/error/missing-communication-kit-format.error';
import type { CommunicationKitFormatConfiguration } from '@client/feature/communication-kit/domain/model/communication-kit-format-configuration.model';
import type { CommunicationKitFormInstance } from '@client/feature/communication-kit/domain/model/communication-kit-form.model';
import type { CommunicationKitLinkedGameInformation } from '@client/feature/communication-kit/domain/model/communication-kit-linked-game-information.model';

export const useCommunicationKitStore = defineStore('communication-kit', () => {
  const communicationKitUiModel = ref<CommunicationKitUiModel>({
    templates: [],
    currentStep: 1,
    isSaveDisabled: true,
  });
  const formInstance = ref<CommunicationKitFormInstance | null>(null);
  const selectedTemplateId = ref<string | null>(null);
  const configuration = shallowRef<CommunicationKitTemplateConfiguration | null>(null);
  const customisationPreviewElements = shallowRef<CommunicationKitCustomisationPreviewElements | null>(null);
  const locale = ref<CommunicationKitLocale>('fr');
  const selectedPreviewFormat = ref<CommunicationKitFormat>(CommunicationKitFormat.Poster);
  const isLoading = ref(false);
  // We keep a counter (instead of a boolean) because the same linked-game fetch can be triggered
  // by multiple independent update appliers (access code / dates / keywords). We want a single
  // global loader that stays visible until all async reactions have completed.
  const linkedGameLoadingCount = ref(0);
  const linkedGameInformationCache = shallowRef(new Map<string, CommunicationKitLinkedGameInformation>());
  const linkedGameInformationInFlight = shallowRef(new Map<string, Promise<CommunicationKitLinkedGameInformation>>());
  const error = ref<string | null>(null);

  const isLinkedGameLoading = computed(() => linkedGameLoadingCount.value > 0);

  const syncSaveDisabled = () => {
    communicationKitUiModel.value = {
      ...communicationKitUiModel.value,
      isSaveDisabled: isLoading.value || !formInstance.value,
    };
  };

  const updateCommunicationKitUiModel = (data: AtLeastOne<CommunicationKitUiModel>) => {
    communicationKitUiModel.value = {
      ...communicationKitUiModel.value,
      ...data,
    };
    syncSaveDisabled();
  };

  const setLoading = (value: boolean) => {
    isLoading.value = value;
    syncSaveDisabled();
  };

  const beginLinkedGameLoading = () => {
    linkedGameLoadingCount.value += 1;
  };

  const endLinkedGameLoading = () => {
    linkedGameLoadingCount.value = Math.max(0, linkedGameLoadingCount.value - 1);
  };

  const getOrFetchLinkedGameInformation = async (
    gameId: string,
    fetcher: () => Promise<CommunicationKitLinkedGameInformation>,
  ): Promise<CommunicationKitLinkedGameInformation> => {
    const cached = linkedGameInformationCache.value.get(gameId);
    if (cached) {
      return cached;
    }

    const inFlight = linkedGameInformationInFlight.value.get(gameId);
    if (inFlight) {
      return inFlight;
    }

    const promise = fetcher()
      .then((info) => {
        linkedGameInformationCache.value.set(gameId, info);
        return info;
      })
      .finally(() => {
        linkedGameInformationInFlight.value.delete(gameId);
      });

    linkedGameInformationInFlight.value.set(gameId, promise);
    return promise;
  };

  const setError = (value: string | null) => {
    error.value = value;
  };

  const setSelectedTemplateId = (value: string | null) => {
    selectedTemplateId.value = value;
  };

  const definedSelectedTemplateId = (): string => {
    const templateId = selectedTemplateId.value;
    if (!templateId) {
      throw new MissingCommunicationKitTemplateError();
    }
    return templateId;
  };

  const definedConfiguration = (): CommunicationKitTemplateConfiguration => {
    if (!configuration.value) {
      throw new MissingCommunicationKitConfigurationError();
    }
    return configuration.value;
  };

  const definedCustomisationPreviewElements = (): CommunicationKitCustomisationPreviewElements => {
    if (!customisationPreviewElements.value) {
      throw new MissingCommunicationKitConfigurationError();
    }
    return customisationPreviewElements.value;
  };

  const definedFormatConfiguration = (format: CommunicationKitFormat): CommunicationKitFormatConfiguration => {
    const configuration = definedConfiguration();
    const formatConfig = configuration.formatsConfigs[format];
    if (!formatConfig) {
      throw new MissingCommunicationKitFormatError(format);
    }
    return formatConfig;
  };

  const setFormInstance = (value: CommunicationKitFormInstance | null) => {
    formInstance.value = value;
    syncSaveDisabled();
  };

  const updateFormInstance = (data: Record<string, unknown>) => {
    const currentFormInstance = formInstance.value;
    if (!currentFormInstance) {
      return;
    }

    for (const [fieldName, value] of Object.entries(data)) {
      const field = (currentFormInstance.fields as unknown as Record<string, { value: unknown }>)[fieldName];
      if (!field) {
        continue;
      }

      field.value = value;
    }
  };

  const setAccessCode = (value: string) => {
    updateFormInstance({ accessCode: value });
  };

  const setDatePeriod = (startDate: Date | null, endDate: Date | null) => {
    updateFormInstance({ startDate, endDate });
  };

  const setLocale = (value: CommunicationKitLocale) => {
    locale.value = value;
  };

  const getLocale = (): CommunicationKitLocale => {
    return locale.value;
  };

  const setConfiguration = (value: CommunicationKitTemplateConfiguration | null) => {
    configuration.value = value;
  };

  const getConfiguration = (): CommunicationKitTemplateConfiguration | null => {
    return configuration.value;
  };

  const setCustomisationPreviewElements = (value: CommunicationKitCustomisationPreviewElements | null) => {
    customisationPreviewElements.value = value;
  };

  const getCustomisationPreviewElements = (): CommunicationKitCustomisationPreviewElements | null => {
    return customisationPreviewElements.value;
  };

  const setSelectedPreviewFormat = (value: CommunicationKitFormat) => {
    selectedPreviewFormat.value = value;
  };

  const getSelectedPreviewFormat = (): CommunicationKitFormat => {
    return selectedPreviewFormat.value;
  };

  const resetForm = () => {
    configuration.value = null;
    customisationPreviewElements.value = null;
    locale.value = 'fr';
    selectedPreviewFormat.value = CommunicationKitFormat.Poster;
    formInstance.value = null;
    linkedGameInformationCache.value.clear();
    linkedGameInformationInFlight.value.clear();
    syncSaveDisabled();
  };

  return {
    communicationKitUiModel,
    formInstance,
    selectedTemplateId,
    configuration,
    customisationPreviewElements,
    locale,
    selectedPreviewFormat,
    isLoading,
    isLinkedGameLoading,
    error,
    updateCommunicationKitUiModel,
    setLoading,
    beginLinkedGameLoading,
    endLinkedGameLoading,
    getOrFetchLinkedGameInformation,
    setError,
    setFormInstance,
    updateFormInstance,
    setAccessCode,
    setDatePeriod,
    setConfiguration,
    getConfiguration,
    setCustomisationPreviewElements,
    getCustomisationPreviewElements,
    setLocale,
    getLocale,
    setSelectedPreviewFormat,
    getSelectedPreviewFormat,
    setSelectedTemplateId,
    definedSelectedTemplateId,
    definedConfiguration,
    definedCustomisationPreviewElements,
    definedFormatConfiguration,
    resetForm,
  };
});
